import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Modal } from 'flowbite';

import SportOfficialsCreateModal from './SportOfficialsCreateModal';
import SportOfficialsIndexTableRow from './SportOfficialsIndexTableRow';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';
import SearchIcon from 'Assets/icons/search.svg';

export default function SportOfficialsIndexTable(props) {
    const [officials, setOfficials] = useState([]);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);

    const [searchValue, setSearchValue] = useState('');

    const [isLoading, setIsLoading] = useState(1);
    const [error, setError] = useState('');

    const [deletedOfficial, setDeletedOfficial] = useState({});

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [sportOfficialsDeleteModalElement, setSportOfficialsDeleteModalElement] = useState(null);
    const [sportOfficialsCreateModalElement, setSportOfficialsCreateModalElement] = useState(null);

    const sportOfficialsDeleteModalRef = useRef();
    const sportOfficialsCreateModalRef = useRef();

    useEffect(() => {
        fetchOfficials();
        sportOfficialsDeleteModalRef && setSportOfficialsDeleteModalElement(new Modal(sportOfficialsDeleteModalRef.current, modalOptions(false)));
        sportOfficialsCreateModalRef && setSportOfficialsCreateModalElement(new Modal(sportOfficialsCreateModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (Object.keys(deletedOfficial).length > 0) {
            showDeleteForm(props.officialRole);
        }
    }, [deletedOfficial]);

    const defaultUrl = props.sportOfficialsIndexUrl + '?role=' + props.officialRole + '&per_page=' + perPage;

    const combineFetchUrl = (p = null, pp = null) => {
        return (
            props.sportOfficialsIndexUrl +
            '?role=' +
            props.officialRole +
            '&per_page=' +
            (pp || perPage) +
            '&page=' +
            (p || page) +
            (searchValue.length > 0 ? '&search=' + searchValue : '')
        );
    };

    const fetchOfficials = (url = null) => {
        setIsLoading(true);
        setError('');
        const fetchUrl = url || defaultUrl;

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data, to, from, total, last_page } = res.data;
                setOfficials(data);
                setTo(to);
                setFrom(from);
                setTotal(total);
                setLastPage(last_page);
                setIsLoading(false);
            })
            .catch((error) => {
                setOfficials([]);
                setIsLoading(false);
                setError('There was an error fetching officials');
            });
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setPage(+data.selected + 1);
        fetchOfficials(url);
    }

    const handleChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            const url = combineFetchUrl(1);
            setPage(1);
            fetchOfficials(url);
        }
    };

    const showCreateForm = (officialRole) => {
        sportOfficialsCreateModalElement && sportOfficialsCreateModalElement.show();
    };

    const showDeleteForm = (officialRole) => {
        sportOfficialsDeleteModalElement && sportOfficialsDeleteModalElement.show();
    };

    const successHandlerCreate = () => {
        if (page === lastPage && officials.length < perPage) {
            setOfficials([]);
            const url = combineFetchUrl(page);
            fetchOfficials(url);
        } else if (page === lastPage && officials.length === perPage) {
            setLastPage(lastPage + 1);
        }
        sportOfficialsCreateModalElement && sportOfficialsCreateModalElement.hide();
    };

    const successHandlerDelete = () => {
        const url = combineFetchUrl(page !== lastPage || officials.length > 1 ? page : page - 1);
        fetchOfficials(url);

        sportOfficialsDeleteModalElement && sportOfficialsDeleteModalElement.hide();
    };

    const closeModal = () => {
        sportOfficialsCreateModalElement && sportOfficialsCreateModalElement.hide();
        sportOfficialsDeleteModalElement && sportOfficialsDeleteModalElement.hide();
    };

    const cleanConfirmationModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const triggerDeleteOfficial = () => {
        setActionInProgress(true);

        axios
            .delete(props.sportOfficialsDeleteUrl.replace('OFFICIAL_ID', deletedOfficial.id))
            .then((res) => {
                setActionCompleted(true);
            })
            .catch((error) => {
                setActionFailed(true);
            });
    };

    return (
        <>
            {error !== '' && (
                <div className="alert alert-danger">
                    <ul>
                        <li>{error}</li>
                    </ul>
                </div>
            )}
            <div className="row d-flex justify-content-end">
                <div className="input-group m-3 col-2">
                    <div className="input-group-prepend">
                        <span className="input-group-text px-3">
                            <SearchIcon className="tw-w-2.5 tw-h-2.5" />
                        </span>
                    </div>
                    <input type="text" className="form-control" value={searchValue} onChange={handleChange} onKeyPress={handleKeyPress}></input>
                </div>
                <div className="m-3">
                    <Button as="a" variant="primary" className="tw-mr-2" onClick={() => showCreateForm(props.officialRole)}>
                        <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                        <span className="pl-1">{$t('Add new official')}</span>
                    </Button>
                </div>
            </div>
            <div className="row d-block">
                <div className={'rounded-15px table-responsive shadow bg-white'}>
                    <table id={'officials-table'} className={'table-without-outside-borders p-0 m-0 table table-bordered no-footer text-center'}>
                        <thead>
                            <tr className="text-blue bg-white">
                                <th>#</th>
                                <th>{$t('First Name')}</th>
                                <th>{$t('Last Name')}</th>
                                <th>{$t('Nationality')}</th>
                                <th>{$t('Gender')}</th>
                                <th>{$t('Role')}</th>
                                <th>{$t('Role Description')}</th>
                                <th>{$t('Arrival Date')}</th>
                                <th>{$t('Departure Date')}</th>
                                <th>{$t('Actions')}</th>
                            </tr>
                        </thead>
                        {!isLoading && (
                            <tbody>
                                {officials.map((official, officialIndex) => {
                                    return (
                                        <SportOfficialsIndexTableRow
                                            key={`officialIndex${officialIndex}`}
                                            officialIndex={(page - 1) * perPage + officialIndex + 1}
                                            official={official}
                                            nationalities={props.nationalities}
                                            officialRole={props.officialRole}
                                            arrivalDate={props.arrivalDate}
                                            departureDate={props.departureDate}
                                            setDeletedOfficial={setDeletedOfficial}
                                            setError={setError}
                                            pageUseTopIndex={Math.ceil((to + from) / 2)}
                                        />
                                    );
                                })}
                            </tbody>
                        )}
                        {!isLoading && officials.length === 0 && (
                            <tbody>
                                <tr>
                                    <td colSpan="10">{$t('No officials created')}</td>
                                </tr>
                            </tbody>
                        )}
                        {isLoading && (
                            <tbody>
                                <tr>
                                    <td colSpan="10">{$t('Loading...')}</td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
                <div className={'d-flex justify-content-between align-items-center py-3'}>
                    <PaginationInfo to={to} from={from} total={total} entries={$t('Officials')} />
                    <TablePagination lastPage={lastPage} pageChange={pageChange} />
                </div>
                <SportOfficialsCreateModal
                    closeHandler={closeModal}
                    successHandler={successHandlerCreate}
                    modalId={`create-sport-official-${props.officialRole}`}
                    modalRef={sportOfficialsCreateModalRef}
                    modalElement={sportOfficialsCreateModalElement}
                    nationalities={props.nationalities}
                    sportOfficialsStoreUrl={props.sportOfficialsStoreUrl}
                    officialRole={props.officialRole}
                />
                <ConfirmationModal
                    closeHandler={closeModal}
                    successHandler={successHandlerDelete}
                    triggerAction={triggerDeleteOfficial}
                    cleanStates={cleanConfirmationModalStates}
                    allowAction={Object.keys(deletedOfficial).length > 0}
                    modalId={`delete-sport-official-${props.officialRole}`}
                    modalRef={sportOfficialsDeleteModalRef}
                    modalElement={sportOfficialsDeleteModalElement}
                    request={actionInProgress}
                    actionCompleted={actionCompleted}
                    actionFailed={actionFailed}
                    confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: `${deletedOfficial?.first_name} ${deletedOfficial?.last_name}` })}
                    actionCompletedMsg={$t('{{name}} deleted', { name: `${deletedOfficial?.first_name} ${deletedOfficial?.last_name}` })}
                    actionFailedMsg={$t('{{name}} could not be deleted', { name: `${deletedOfficial?.first_name} ${deletedOfficial?.last_name}` })}
                    title={$t('Delete Official')}
                    cancelButtonText="Cancel"
                    primaryButtonText="Delete"
                />
            </div>
        </>
    );
}
